export interface MusicAnalysis {
  bpm: number;
  beats: number[];
  envelope: number[];
  accents: number[];
  step: number;
  duration: number;
  quietFraction: number;
}
export interface MusicCue {
  energy: number;
  accent: number;
  quiet: boolean;
}

// RMS and positive energy changes, normalized against this particular preview.
// This keeps compressed and quiet recordings comparable without inventing notes.
export function analyseDynamics(pcm: Float32Array, sampleRate = 44100) {
  const step = 0.05;
  const hop = Math.round(sampleRate * step);
  const rms: number[] = [];
  for (let start = 0; start < pcm.length; start += hop) {
    let sum = 0;
    const end = Math.min(start + hop, pcm.length);
    for (let i = start; i < end; i++) sum += pcm[i] * pcm[i];
    rms.push(Math.sqrt(sum / Math.max(1, end - start)));
  }
  const sorted = [...rms].sort((a, b) => a - b);
  const high = Math.max(0.001, sorted[Math.floor(sorted.length * 0.9)] || 0);
  const envelope = rms.map((value) => Math.min(1, value / high));
  const accents = envelope.map((value, i) =>
    Math.min(1, Math.max(0, value - (envelope[Math.max(0, i - 2)] || 0)) * 3)
  );
  return {
    step,
    envelope,
    accents,
    duration: pcm.length / sampleRate,
    quietFraction: envelope.filter((value) => value < 0.3).length / Math.max(1, envelope.length),
  };
}

export function musicCueAt(analysis: MusicAnalysis, seconds: number): MusicCue {
  const time = ((seconds % analysis.duration) + analysis.duration) % analysis.duration;
  const index = Math.min(analysis.envelope.length - 1, Math.floor(time / analysis.step));
  const energy = analysis.envelope[index] ?? 0;
  return {
    energy,
    accent: Math.max(...analysis.accents.slice(Math.max(0, index - 1), index + 2), 0),
    quiet: energy < 0.3,
  };
}

export function calibrationOffset(taps: number[], start: number, interval = 500): number | null {
  if (taps.length < 8) return null;
  const offsets = taps
    .slice(2)
    .map((t) => ((((t - start + interval / 2) % interval) + interval) % interval) - interval / 2)
    .sort((a, b) => a - b);
  const median = offsets[Math.floor(offsets.length / 2)];
  const deviation = offsets.map((value) => Math.abs(value - median)).sort((a, b) => a - b);
  return deviation[Math.floor(deviation.length / 2)] > 60
    ? null
    : Math.max(-200, Math.min(200, Math.round(median)));
}
